import React from 'react';
import cx from 'classnames';
import { Root as TabsRoot } from '@radix-ui/react-tabs';
import {
  ComponentTreeNode,
  FlexiblePageTreeNodeType,
  isTabListTreeNode,
  isTabPanelsTreeNode,
  TabGroupTreeNodeDataProps,
  TabListTreeNode,
  TabPanelsTreeNode,
} from '@mutiny-pkg/champaign-schemas/src/schemas/flexiblePages/flexiblePagesTreeNode';

type TabGroupProps = {
  id: string;
  editorClassName?: string;
  isEditor?: boolean;
  renderComponentNode: (treeNode: ComponentTreeNode, parentId: string, index: number) => React.ReactNode;
  tabGroupNodes: [] | [TabListTreeNode, TabPanelsTreeNode] | [TabPanelsTreeNode, TabListTreeNode];
} & TabGroupTreeNodeDataProps;

type TabGroupContextValue = {
  activeIndex: number;
  setActiveIndex: (i: number) => void;
  tabIds: string[];
  panelIds: string[];
};
export const TabGroupContext = React.createContext<TabGroupContextValue | null>(null);

const tabValue = (i: number) => `tab-${i}`;

export const TabGroup = React.memo(
  React.forwardRef<HTMLDivElement, TabGroupProps>(
    (
      {
        id,
        className,
        editorClassName,
        isEditor: _isEditor,
        renderComponentNode,
        tabGroupNodes,
        // `type` is a tree-node schema discriminator, not a DOM attribute — destructure it out so
        // it doesn't land on the underlying <div> and trigger an unknown-attribute warning.
        type: _type,
        ...props
      },
      ref,
    ) => {
      const [activeIndex, setActiveIndex] = React.useState(0);

      const allNodes: ReadonlyArray<TabListTreeNode | TabPanelsTreeNode> = tabGroupNodes;
      const tabListNode = allNodes.find(isTabListTreeNode);
      const tabPanelsNode = allNodes.find(isTabPanelsTreeNode);

      // Truncate so each tab has a matching panel — index becomes the Radix `value` and must align.
      const minChildCount = Math.min(tabListNode?.children?.length ?? 0, tabPanelsNode?.children?.length ?? 0);
      const safeActive = Math.min(activeIndex, Math.max(0, minChildCount - 1));

      const tabIds = React.useMemo(
        () => tabListNode?.children?.slice(0, minChildCount).map((c) => c.id) ?? [],
        [tabListNode?.children, minChildCount],
      );

      const panelIds = React.useMemo(
        () => tabPanelsNode?.children?.slice(0, minChildCount).map((c) => c.id) ?? [],
        [tabPanelsNode?.children, minChildCount],
      );

      const ctxValue = React.useMemo<TabGroupContextValue>(
        () => ({ activeIndex: safeActive, setActiveIndex, tabIds, panelIds }),
        [safeActive, tabIds, panelIds],
      );

      if (!tabListNode || !tabPanelsNode) {
        console.warn('TabGroup requires both TabList and TabPanels nodes');
        return null;
      }

      const updatedTabListNode: TabListTreeNode = {
        ...tabListNode,
        children: tabListNode.children?.slice(0, minChildCount) ?? [],
      };
      const updatedTabPanelsNode: TabPanelsTreeNode = {
        ...tabPanelsNode,
        children: tabPanelsNode.children?.slice(0, minChildCount) ?? [],
      };

      const updatedTabGroupNodes: [TabListTreeNode, TabPanelsTreeNode] | [TabPanelsTreeNode, TabListTreeNode] =
        tabGroupNodes[0]?.data?.type === FlexiblePageTreeNodeType.TabList
          ? [updatedTabListNode, updatedTabPanelsNode]
          : [updatedTabPanelsNode, updatedTabListNode];

      return (
        <TabGroupContext.Provider value={ctxValue}>
          <TabsRoot
            value={tabValue(safeActive)}
            onValueChange={(v) => {
              const next = Number(v.replace(/^tab-/, ''));
              if (Number.isFinite(next)) setActiveIndex(next);
            }}
            asChild
          >
            <div ref={ref} id={id} className={cx(className, editorClassName)} {...props}>
              {updatedTabGroupNodes.map((node, nodeIndex) => renderComponentNode(node, id, nodeIndex))}
            </div>
          </TabsRoot>
        </TabGroupContext.Provider>
      );
    },
  ),
);

TabGroup.displayName = 'TabGroup';
